/**
 * Quote Search Service
 *
 * Three search modes mirrored from the mobile QuotesScreen:
 *   - by date     → matches departure_date OR return_date (two parallel calls + dedup)
 *   - by airport  → matches departure_airport OR arrival_airport (two parallel calls + dedup)
 *   - by client   → matches client_id (single call)
 *
 * Each function paginates via `skip` and `limit` and returns
 *   { data: Quote[], hasMore: boolean }
 * so the caller can drive an infinite-scroll / Load More UI.
 *
 * For the OR-via-two-calls cases (date/airport), `hasMore` is true when EITHER
 * underlying call returned exactly `limit` items — that's a signal that more
 * pages may be available. Cross-page duplicates can still occur (a quote in
 * page N of the departure query may resurface in page N+1 of the arrival
 * query); the caller is expected to dedup globally by quote_id.
 */
import { apiClient } from '@/lib/api';

function dedupeByQuoteId(...lists) {
  const map = new Map();
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const q of list) {
      const id = q.quote_id || q.id;
      if (id && !map.has(id)) map.set(id, q);
    }
  }
  return Array.from(map.values());
}

function asArray(x) {
  return Array.isArray(x) ? x : [];
}

/**
 * Search quotes whose departure_date OR return_date matches the given day.
 */
export async function searchQuotesByDate(date, scopeFilter = {}, skip = 0, limit = 10) {
  if (!date) return { data: [], hasMore: false };
  const [departure, ret] = await Promise.all([
    apiClient.getQuotes(skip, limit, {
      ...scopeFilter,
      departure_date_from: date,
      departure_date_to: date,
    }),
    apiClient.getQuotes(skip, limit, {
      ...scopeFilter,
      return_date_from: date,
      return_date_to: date,
    }),
  ]);
  const dep = asArray(departure);
  const r = asArray(ret);
  return {
    data: dedupeByQuoteId(dep, r),
    hasMore: dep.length === limit || r.length === limit,
  };
}

/**
 * Search quotes whose departure_airport OR arrival_airport matches the ICAO code.
 */
export async function searchQuotesByAirport(airportCode, scopeFilter = {}, skip = 0, limit = 10) {
  if (!airportCode) return { data: [], hasMore: false };
  const code = airportCode.trim().toUpperCase();
  const [departure, arrival] = await Promise.all([
    apiClient.getQuotes(skip, limit, {
      ...scopeFilter,
      departure_airport: code,
    }),
    apiClient.getQuotes(skip, limit, {
      ...scopeFilter,
      arrival_airport: code,
    }),
  ]);
  const dep = asArray(departure);
  const arr = asArray(arrival);
  return {
    data: dedupeByQuoteId(dep, arr),
    hasMore: dep.length === limit || arr.length === limit,
  };
}

/**
 * Search quotes for a specific client_id.
 */
export async function searchQuotesByClient(clientId, scopeFilter = {}, skip = 0, limit = 10) {
  if (!clientId) return { data: [], hasMore: false };
  const list = asArray(
    await apiClient.getQuotes(skip, limit, {
      ...scopeFilter,
      client_id: clientId,
    })
  );
  return {
    data: dedupeByQuoteId(list),
    hasMore: list.length === limit,
  };
}
